import React, { Suspense, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import NotFoundPage from "../pages/notFound";
import LoginPage from "../pages/login";
import PageLoader from "../components/PageLoader";
import SignUpPage from "../pages/signup";
import CompanyDetailsPage from "../pages/companyDetails";
import EmailVerificationPage from "../pages/emailVerification";
import ForgotPasswordPage from "../pages/forgotPassword";
import AddOperatorAddress from "../pages/operators/operatorAddress";
import AddVehicles from "../pages/vehicles/addVehicles";
import EnterUserNamePage from "../pages/enterUserName";
import VehiclesList from "../pages/vehicles/vehiclesList";
import VehicleDetails from "../pages/vehicles/vehicleDetails";
import OwnerDashboard from "../pages/ownersDashboard/ownerDashboard";
import InviteDrivers from "../pages/drivers/inviteDrivers";
import UploadDocuments from "../pages/uploadDocuments";
import OperatorAccountDetails from "../pages/operatorSignUp/accountDetails";
import OperatorAddressDetails from "../pages/operatorSignUp/addressDetails";
import OperatorUploadDocuments from "../pages/operatorSignUp/uploadDocuments";
import DriversList from "../pages/drivers/driverList";
import DriverDetails from "../pages/drivers/driverDetails";
import EditDriver from "../pages/drivers/editDriver";
import EditVehicle from "../pages/vehicles/editVehicle/updateVehicle";
import VerifyUser from "../pages/verifyUser/verifyUser";
import ReservationVehical from "../pages/reservationFlow/reservation";
import MakeReservationPage from "../pages/reservationFlow/makeReservation";
import ReservationTypePage from "../pages/reservationFlow/reservationType";
import StorageAgreeementPage from "../pages/reservationFlow/storageAgreeement";
import OperatorSignup from "../pages/operators/operatorSignup";
import CheckOutReservationPage from "../pages/reservationFlow/checkOutReservation";
import UploadReservationDocumentsPage from "../pages/reservationFlow/uploadReservationDocuments";
import ReservationDetailsPage from "../pages/reservationFlow/reservationDetails";
import OperatorDashboard from "../pages/operatorDashboard";
import AdobeDocument from "../pages/signupDocument/adobeDocument";
import ProfileView from "../pages/profile";
import EditProfile from "../pages/editProfile";
import storePersist, { localConstant } from "../services/localStorage";
import ExtendReservationPage from "../pages/reservationFlow/extendReservation";
import MyDocuments from "../pages/myDocuments";
import CancelReservationPage from "../pages/reservationFlow/cancelReservationScreen";
import MakePayment from "../pages/payments/makePayment";
import ESignVerification from "../pages/signupDocument/eSignVerification";
import { useSelector } from "react-redux";
import { BillingPayments } from "../pages/BillingPayments";
import { InvoiceDetails } from "../pages/BillingPayments/invoiceDetails";
import InvoicePayment from "../pages/BillingPayments/InvoicePayment/invoicePayment";
import { useRouteChange } from "../hooks/useRouteChange";
import Maintenance from "../pages/Maintenance";
import GatesListForAllLocations from "../pages/GatesListForAllLocations";

const ProtectedRoute = () => {
  // Assuming the access token is stored in localStorage
  const accessToken = storePersist.get(localConstant.API_ACCESS_TOKEN);

  // If there is no access token, redirect to the login page
  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }

  // If there is an access token, render the children components
  return <Outlet />;
};

const RestrictedRoute = ({ allowedTypes }) => {
  // Simulate fetching the current user's type
  // This could be from local storage, context, Redux, etc.
  const currentUserType = storePersist.get(localConstant.USER_PROFILE);

  // Check if the current user's type is allowed to access the route
  const isAllowed = allowedTypes.includes(currentUserType);

  if (!isAllowed) {
    // Redirect to the login page if the user type is not allowed
    return <Navigate to="/login" replace />;
  }

  // Render children routes if the user type is allowed
  return <Outlet />;
};

export default function AppRouter({ setAppSettings }) {
  const getnetWorkState = () => {
    if (!window) {
      return false;
    }
    return window?.navigator?.onLine;
  };
  const redux = useSelector((state) => state);
  const [networkStatus, setNetworkState] = useState(getnetWorkState());
  useEffect(() => {
    window.addEventListener("online", () => setNetworkState());
    window.addEventListener("offline", () => setNetworkState(false));
  }, []);

  useRouteChange({ event1: setAppSettings });
  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait" initial={false}>
        <Router>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route
              path="/companyDetailsPage"
              element={<CompanyDetailsPage />}
            />
            <Route
              path="/emailVerification"
              element={<EmailVerificationPage />}
            />
            <Route path="/enter_username" element={<EnterUserNamePage />} />
            <Route path="/forgot_password" element={<ForgotPasswordPage />} />
            <Route
              path="*"
              component={NotFoundPage}
              render={() => <Navigate to="/notfound" />}
            />
            <Route
              path="/operatorAccountDetails"
              element={<OperatorAccountDetails />}
            />
            <Route
              path="/operatorAddressDetails"
              element={<OperatorAddressDetails />}
            />
            <Route
              path="/operatorUploadDocuments"
              element={<OperatorUploadDocuments />}
            />
            <Route path="/verifyUser" element={<VerifyUser />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<RestrictedRoute allowedTypes={["OWNER"]} />}>
                <Route path="/addVehicles" element={<AddVehicles />} />
                <Route path="/vehiclesList" element={<VehiclesList />} />
                <Route path="/vehicleDetails" element={<VehicleDetails />} />
                <Route path="/editVehicle" element={<EditVehicle />} />
                <Route path="/ownerDashboard" element={<OwnerDashboard />} />
                <Route path="/inviteDrivers" element={<InviteDrivers />} />
                <Route path="/driversList" element={<DriversList />} />
                <Route path="/driverDetails" element={<DriverDetails />} />
                <Route path="/editDriver" element={<EditDriver />} />
                <Route
                  path="/makeReservation"
                  element={<MakeReservationPage />}
                />
                <Route
                  path="/reservationType"
                  element={<ReservationTypePage />}
                />
                <Route
                  path="/check_out_reservation"
                  element={<CheckOutReservationPage />}
                />
                <Route path="/makePayment" element={<MakePayment />} />
                <Route
                  path="/upload_reservation_doc"
                  element={<UploadReservationDocumentsPage />}
                />
                <Route path="/adobeDocument" element={<AdobeDocument />} />
                <Route
                  path="/eSignVerification"
                  element={<ESignVerification />}
                />
              </Route>
              <Route path="/uploadDocuments" element={<UploadDocuments />} />
              <Route element={<RestrictedRoute allowedTypes={["DRIVER"]} />}>
                <Route
                  path="/addOperatorAddress"
                  element={<AddOperatorAddress />}
                />
                <Route path="/operatorSignup" element={<OperatorSignup />} />
                <Route
                  path="/operatorDashboard"
                  element={<OperatorDashboard />}
                />
              </Route>
              <Route path="/documents" element={<MyDocuments />} />
              {storePersist.get(localConstant.SUPER_ADMIN) && (
                <Route
                  path="/gates_list"
                  element={<GatesListForAllLocations />}
                />
              )}
              <Route path="/reservation" element={<ReservationVehical />} />
              <Route
                path="/reservation_details"
                element={<ReservationDetailsPage />}
              />
              <Route path="/billing_payments" element={<BillingPayments />} />
              <Route
                path="/billing_payments/details"
                element={<InvoiceDetails />}
              />
              <Route
                path="/billing_payments/payment"
                element={<InvoicePayment />}
              />
              <Route
                path="/storage_agreeement"
                element={<StorageAgreeementPage />}
              />
              <Route path="/profileView" element={<ProfileView />} />
              <Route path="/editProfile" element={<EditProfile />} />
              <Route
                path="/extendReservation"
                element={<ExtendReservationPage />}
              />
              <Route
                path="/cancelReservation"
                element={<CancelReservationPage />}
              />
            </Route>
          </Routes>
        </Router>
      </AnimatePresence>
    </Suspense>
  );
}
